/**
 * Sync Store
 *
 * Zustand store for managing synchronization state between HVAC graph and IDF store.
 * Handles bidirectional sync coordination, conflict detection, and state management.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { SyncState, SyncConfig } from './types';
import {
  createIDFObjectFromNode,
  updateIDFObjectFromNode,
  calculatePortNodeNames,
  idfObjectToNodeData,
  findHVACObjects,
  matchObjectsToNodes,
  createEdgeSyncData,
} from './syncService';
import { useIDFStore } from '../../stores/idfStore';
import { useHVACStore } from '../hvacStore';

interface SyncStoreState extends SyncState {
  /** Sync configuration */
  config: SyncConfig;

  // Actions

  /** Enable/disable sync */
  setEnabled: (enabled: boolean) => void;

  /** Update sync configuration */
  updateConfig: (config: Partial<SyncConfig>) => void;

  /** Sync a single node to IDF (create or update) */
  syncNodeToIDF: (nodeId: string) => string | null;

  /** Sync a single IDF object to graph (create or update) */
  syncObjectToGraph: (objectId: string) => string | null;

  /** Sync all graph nodes to IDF */
  syncAllNodesToIDF: () => void;

  /** Sync all IDF objects to graph */
  syncAllObjectsToGraph: () => void;

  /** Remove IDF object when node is deleted */
  handleNodeDeleted: (nodeId: string) => void;

  /** Handle IDF object deletion */
  handleObjectDeleted: (objectId: string) => void;

  /** Sync edge changes (update port node names in IDF objects) */
  syncEdgeToIDF: (edgeId: string) => void;

  /** Handle edge deletion */
  handleEdgeDeleted: (edgeId: string) => void;

  /** Link a node to an existing IDF object */
  linkNodeToObject: (nodeId: string, objectId: string) => void;

  /** Unlink a node from its IDF object */
  unlinkNode: (nodeId: string) => void;

  /** Clear all sync state */
  clearSync: () => void;

  /** Resolve a conflict */
  resolveConflict: (entityId: string, resolution: 'use_graph' | 'use_idf') => void;

  /** Get linked IDF object ID for a node */
  getObjectIdForNode: (nodeId: string) => string | undefined;

  /** Get linked node ID for an IDF object */
  getNodeIdForObject: (objectId: string) => string | undefined;

  /** Check if node is synced */
  isNodeSynced: (nodeId: string) => boolean;

  /** Check if object is synced */
  isObjectSynced: (objectId: string) => boolean;

  /** Perform initial sync between existing graph and IDF */
  performInitialSync: () => void;
}

/**
 * Default sync configuration
 */
const defaultConfig: SyncConfig = {
  autoSyncFromGraph: true,
  autoSyncFromIDF: true,
  debounceMs: 300,
  defaultConflictResolution: 'use_graph',
  createMissingObjects: true,
  createMissingNodes: false,
};

/**
 * Synchronous lock to prevent concurrent sync operations.
 * Using a module-level variable ensures atomicity in single-threaded JS.
 */
let syncLock = false;

export const useSyncStore = create<SyncStoreState>()(
  devtools(
    (set, get) => ({
      // Initial state
      enabled: true,
      isSyncing: false,
      lastSyncTime: null,
      pendingEvents: [],
      conflicts: [],
      nodeToObject: new Map(),
      objectToNode: new Map(),
      edgeSyncData: new Map(),
      config: defaultConfig,

      setEnabled: (enabled: boolean) => {
        set({ enabled });
      },

      updateConfig: (configUpdates: Partial<SyncConfig>) => {
        set((state) => ({
          config: { ...state.config, ...configUpdates },
        }));
      },

      syncNodeToIDF: (nodeId: string) => {
        const state = get();
        if (!state.enabled || syncLock) return null;

        // Acquire lock synchronously to prevent race conditions
        syncLock = true;
        // Clear any previous error when starting new sync
        set({ isSyncing: true, error: undefined });

        try {
          const hvacStore = useHVACStore.getState();
          const idfStore = useIDFStore.getState();

          const node = hvacStore.nodes.find((n) => n.id === nodeId);
          if (!node) {
            console.warn(`Node not found: ${nodeId}`);
            set({ isSyncing: false });
            syncLock = false;
            return null;
          }

          // Calculate port node names from edges
          const portNodeNames = calculatePortNodeNames(
            nodeId,
            hvacStore.edges,
            hvacStore.nodes
          );

          // Check if node is already linked to an IDF object
          const existingObjectId = state.nodeToObject.get(nodeId);

          if (existingObjectId) {
            // Update existing IDF object
            const existingObject = idfStore.getObjectById(existingObjectId);
            if (existingObject) {
              const updatedObject = updateIDFObjectFromNode(
                existingObject,
                node,
                portNodeNames
              );
              idfStore.updateObject(existingObjectId, {
                fields: updatedObject.fields,
              });
              set({ lastSyncTime: Date.now(), isSyncing: false });
              syncLock = false;
              return existingObjectId;
            }
          }

          // Create new IDF object
          const newObject = createIDFObjectFromNode(node, portNodeNames);
          if (!newObject) {
            console.warn(`Failed to create IDF object for node: ${nodeId}`);
            set({ isSyncing: false });
            syncLock = false;
            return null;
          }

          // Add to IDF store
          const newObjectId = idfStore.addObject({
            className: newObject.className,
            fields: newObject.fields,
            leadingComments: [],
          });

          // Update mappings
          const newNodeToObject = new Map(state.nodeToObject);
          const newObjectToNode = new Map(state.objectToNode);
          newNodeToObject.set(nodeId, newObjectId);
          newObjectToNode.set(newObjectId, nodeId);

          // Update node with idfObjectId
          hvacStore.updateNodeData(nodeId, { idfObjectId: newObjectId });

          set({
            nodeToObject: newNodeToObject,
            objectToNode: newObjectToNode,
            lastSyncTime: Date.now(),
            isSyncing: false,
          });
          syncLock = false;

          return newObjectId;
        } catch (error) {
          console.error('Error syncing node to IDF:', error);
          set({ isSyncing: false, error: String(error) });
          syncLock = false;
          return null;
        }
      },

      syncObjectToGraph: (objectId: string) => {
        const state = get();
        if (!state.enabled || syncLock) return null;

        // Acquire lock synchronously to prevent race conditions
        syncLock = true;
        // Clear any previous error when starting new sync
        set({ isSyncing: true, error: undefined });

        try {
          const hvacStore = useHVACStore.getState();
          const idfStore = useIDFStore.getState();

          const object = idfStore.getObjectById(objectId);
          if (!object) {
            console.warn(`IDF object not found: ${objectId}`);
            set({ isSyncing: false });
            syncLock = false;
            return null;
          }

          // Convert IDF object to node data
          const nodeData = idfObjectToNodeData(object);
          if (!nodeData) {
            // Not an HVAC-related object
            set({ isSyncing: false });
            syncLock = false;
            return null;
          }

          // Check if object is already linked to a node
          const existingNodeId = state.objectToNode.get(objectId);

          if (existingNodeId) {
            // Update existing node
            const existingNode = hvacStore.getNode(existingNodeId);
            if (existingNode) {
              hvacStore.updateNodeData(existingNodeId, {
                name: nodeData.name,
                fields: nodeData.fields,
              });
              set({ lastSyncTime: Date.now(), isSyncing: false });
              syncLock = false;
              return existingNodeId;
            }
          }

          // Create new node (only if config allows)
          if (!state.config.createMissingNodes) {
            set({ isSyncing: false });
            syncLock = false;
            return null;
          }

          // Create node at default position
          const newNodeId = hvacStore.addNode(
            nodeData.componentType,
            { x: 100 + Math.random() * 200, y: 100 + Math.random() * 200 },
            nodeData.name
          );

          // Update node fields
          hvacStore.updateNodeData(newNodeId, {
            fields: nodeData.fields,
            idfObjectId: objectId,
          });

          // Update mappings
          const newNodeToObject = new Map(state.nodeToObject);
          const newObjectToNode = new Map(state.objectToNode);
          newNodeToObject.set(newNodeId, objectId);
          newObjectToNode.set(objectId, newNodeId);

          set({
            nodeToObject: newNodeToObject,
            objectToNode: newObjectToNode,
            lastSyncTime: Date.now(),
            isSyncing: false,
          });
          syncLock = false;

          return newNodeId;
        } catch (error) {
          console.error('Error syncing object to graph:', error);
          set({ isSyncing: false, error: String(error) });
          syncLock = false;
          return null;
        }
      },

      syncAllNodesToIDF: () => {
        const state = get();
        if (!state.enabled) return;

        // Don't set isSyncing to avoid blocking individual sync calls
        try {
          const hvacStore = useHVACStore.getState();
          const idfStore = useIDFStore.getState();

          const newNodeToObject = new Map(state.nodeToObject);
          const newObjectToNode = new Map(state.objectToNode);

          for (const node of hvacStore.nodes) {
            // Skip if already synced
            if (newNodeToObject.has(node.id)) continue;

            // Calculate port node names from edges
            const portNodeNames = calculatePortNodeNames(
              node.id,
              hvacStore.edges,
              hvacStore.nodes
            );

            // Create new IDF object
            const newObject = createIDFObjectFromNode(node, portNodeNames);
            if (!newObject) continue;

            // Add to IDF store
            const newObjectId = idfStore.addObject({
              className: newObject.className,
              fields: newObject.fields,
              leadingComments: [],
            });

            // Update mappings
            newNodeToObject.set(node.id, newObjectId);
            newObjectToNode.set(newObjectId, node.id);

            // Update node with idfObjectId
            hvacStore.updateNodeData(node.id, { idfObjectId: newObjectId });
          }

          set({
            nodeToObject: newNodeToObject,
            objectToNode: newObjectToNode,
            lastSyncTime: Date.now(),
          });
        } catch (error) {
          console.error('Error syncing all nodes to IDF:', error);
          set({ error: String(error) });
        }
      },

      syncAllObjectsToGraph: () => {
        const state = get();
        if (!state.enabled) return;

        try {
          const idfStore = useIDFStore.getState();
          const hvacStore = useHVACStore.getState();
          const document = idfStore.document;

          if (!document) {
            return;
          }

          // Find HVAC-related objects
          const hvacObjects = findHVACObjects(document.objects);

          const newNodeToObject = new Map(state.nodeToObject);
          const newObjectToNode = new Map(state.objectToNode);

          for (const object of hvacObjects) {
            // Skip if already synced
            if (newObjectToNode.has(object.id)) continue;

            // Skip if config doesn't allow creating nodes
            if (!state.config.createMissingNodes) continue;

            // Convert IDF object to node data
            const nodeData = idfObjectToNodeData(object);
            if (!nodeData) continue;

            // Create node at default position
            const newNodeId = hvacStore.addNode(
              nodeData.componentType,
              { x: 100 + Math.random() * 200, y: 100 + Math.random() * 200 },
              nodeData.name
            );

            // Update node fields
            hvacStore.updateNodeData(newNodeId, {
              fields: nodeData.fields,
              idfObjectId: object.id,
            });

            // Update mappings
            newNodeToObject.set(newNodeId, object.id);
            newObjectToNode.set(object.id, newNodeId);
          }

          set({
            nodeToObject: newNodeToObject,
            objectToNode: newObjectToNode,
            lastSyncTime: Date.now(),
          });
        } catch (error) {
          console.error('Error syncing all objects to graph:', error);
          set({ error: String(error) });
        }
      },

      handleNodeDeleted: (nodeId: string) => {
        const state = get();

        // Get linked object ID
        const objectId = state.nodeToObject.get(nodeId);

        // Remove from mappings
        const newNodeToObject = new Map(state.nodeToObject);
        const newObjectToNode = new Map(state.objectToNode);
        newNodeToObject.delete(nodeId);

        if (objectId) {
          newObjectToNode.delete(objectId);

          // Optionally delete the IDF object
          if (state.config.autoSyncFromGraph) {
            const idfStore = useIDFStore.getState();
            idfStore.removeObject(objectId);
          }
        }

        // Remove edge sync data for this node
        const newEdgeSyncData = new Map(state.edgeSyncData);
        for (const [edgeId, data] of newEdgeSyncData.entries()) {
          if (data.sourceObjectId === objectId || data.targetObjectId === objectId) {
            newEdgeSyncData.delete(edgeId);
          }
        }

        set({
          nodeToObject: newNodeToObject,
          objectToNode: newObjectToNode,
          edgeSyncData: newEdgeSyncData,
        });
      },

      handleObjectDeleted: (objectId: string) => {
        const state = get();

        // Get linked node ID
        const nodeId = state.objectToNode.get(objectId);

        // Remove from mappings
        const newNodeToObject = new Map(state.nodeToObject);
        const newObjectToNode = new Map(state.objectToNode);
        newObjectToNode.delete(objectId);

        if (nodeId) {
          newNodeToObject.delete(nodeId);

          // Update node to clear idfObjectId by setting it to empty string
          // (will be treated as unlinked)
          const hvacStore = useHVACStore.getState();
          const node = hvacStore.getNode(nodeId);
          if (node) {
            const { idfObjectId: _, ...restData } = node.data;
            hvacStore.updateNodeData(nodeId, restData);
          }
        }

        set({
          nodeToObject: newNodeToObject,
          objectToNode: newObjectToNode,
        });
      },

      syncEdgeToIDF: (edgeId: string) => {
        const state = get();
        if (!state.enabled) return;

        const hvacStore = useHVACStore.getState();
        const edge = hvacStore.edges.find((e) => e.id === edgeId);

        if (!edge || !edge.data) return;

        // Get connected nodes
        const sourceNode = hvacStore.nodes.find((n) => n.id === edge.source);
        const targetNode = hvacStore.nodes.find((n) => n.id === edge.target);

        if (!sourceNode || !targetNode) return;

        // Get linked IDF object IDs
        const sourceObjectId = state.nodeToObject.get(edge.source);
        const targetObjectId = state.nodeToObject.get(edge.target);

        // Update port node names in both connected objects
        if (sourceObjectId) {
          get().syncNodeToIDF(edge.source);
        }

        if (targetObjectId) {
          get().syncNodeToIDF(edge.target);
        }

        // Store edge sync data
        if (sourceObjectId && targetObjectId) {
          const syncData = createEdgeSyncData(
            edge,
            sourceNode,
            targetNode,
            sourceObjectId,
            targetObjectId
          );

          if (syncData) {
            const newEdgeSyncData = new Map(state.edgeSyncData);
            newEdgeSyncData.set(edgeId, syncData);
            set({ edgeSyncData: newEdgeSyncData });
          }
        }
      },

      handleEdgeDeleted: (edgeId: string) => {
        const state = get();

        const edgeData = state.edgeSyncData.get(edgeId);
        if (!edgeData) return;

        // Find the nodes that were connected by this edge
        // and re-sync them to clear the port node names
        const sourceNodeId = state.objectToNode.get(edgeData.sourceObjectId);
        const targetNodeId = state.objectToNode.get(edgeData.targetObjectId);

        if (sourceNodeId) {
          get().syncNodeToIDF(sourceNodeId);
        }

        if (targetNodeId) {
          get().syncNodeToIDF(targetNodeId);
        }

        // Remove edge sync data
        const newEdgeSyncData = new Map(state.edgeSyncData);
        newEdgeSyncData.delete(edgeId);
        set({ edgeSyncData: newEdgeSyncData });
      },

      linkNodeToObject: (nodeId: string, objectId: string) => {
        const state = get();

        const newNodeToObject = new Map(state.nodeToObject);
        const newObjectToNode = new Map(state.objectToNode);

        // Remove any existing links
        const existingObjectId = newNodeToObject.get(nodeId);
        if (existingObjectId) {
          newObjectToNode.delete(existingObjectId);
        }

        const existingNodeId = newObjectToNode.get(objectId);
        if (existingNodeId) {
          newNodeToObject.delete(existingNodeId);
        }

        // Create new link
        newNodeToObject.set(nodeId, objectId);
        newObjectToNode.set(objectId, nodeId);

        // Update node with idfObjectId
        const hvacStore = useHVACStore.getState();
        hvacStore.updateNodeData(nodeId, { idfObjectId: objectId });

        set({
          nodeToObject: newNodeToObject,
          objectToNode: newObjectToNode,
        });
      },

      unlinkNode: (nodeId: string) => {
        const state = get();

        const objectId = state.nodeToObject.get(nodeId);
        if (!objectId) return;

        const newNodeToObject = new Map(state.nodeToObject);
        const newObjectToNode = new Map(state.objectToNode);

        newNodeToObject.delete(nodeId);
        newObjectToNode.delete(objectId);

        // Update node to clear idfObjectId
        const hvacStore = useHVACStore.getState();
        const node = hvacStore.getNode(nodeId);
        if (node) {
          const { idfObjectId: _, ...restData } = node.data;
          hvacStore.updateNodeData(nodeId, restData);
        }

        set({
          nodeToObject: newNodeToObject,
          objectToNode: newObjectToNode,
        });
      },

      clearSync: () => {
        set({
          nodeToObject: new Map(),
          objectToNode: new Map(),
          edgeSyncData: new Map(),
          conflicts: [],
          pendingEvents: [],
          lastSyncTime: null,
        });
      },

      resolveConflict: (entityId: string, resolution: 'use_graph' | 'use_idf') => {
        const state = get();

        const conflict = state.conflicts.find((c) => c.entityId === entityId);
        if (!conflict) return;

        // Apply resolution
        if (resolution === 'use_graph') {
          // Sync graph to IDF
          const nodeId = state.objectToNode.get(entityId) || entityId;
          get().syncNodeToIDF(nodeId);
        } else {
          // Sync IDF to graph
          const objectId = state.nodeToObject.get(entityId) || entityId;
          get().syncObjectToGraph(objectId);
        }

        // Remove conflict
        set({
          conflicts: state.conflicts.filter((c) => c.entityId !== entityId),
        });
      },

      getObjectIdForNode: (nodeId: string) => {
        return get().nodeToObject.get(nodeId);
      },

      getNodeIdForObject: (objectId: string) => {
        return get().objectToNode.get(objectId);
      },

      isNodeSynced: (nodeId: string) => {
        return get().nodeToObject.has(nodeId);
      },

      isObjectSynced: (objectId: string) => {
        return get().objectToNode.has(objectId);
      },

      performInitialSync: () => {
        const state = get();
        if (!state.enabled || syncLock) return;

        // Acquire lock synchronously to prevent race conditions
        syncLock = true;
        // Clear any previous error before starting new sync
        set({ isSyncing: true, error: undefined });

        try {
          const hvacStore = useHVACStore.getState();
          const idfStore = useIDFStore.getState();
          const document = idfStore.document;

          // Match existing IDF objects to graph nodes by name
          if (document) {
            const hvacObjects = findHVACObjects(document.objects);
            const matches = matchObjectsToNodes(hvacObjects, hvacStore.nodes);

            const newNodeToObject = new Map(state.nodeToObject);
            const newObjectToNode = new Map(state.objectToNode);

            for (const [objectId, nodeId] of matches.entries()) {
              newNodeToObject.set(nodeId, objectId);
              newObjectToNode.set(objectId, nodeId);

              // Update node with idfObjectId
              hvacStore.updateNodeData(nodeId, { idfObjectId: objectId });
            }

            set({
              nodeToObject: newNodeToObject,
              objectToNode: newObjectToNode,
            });
          }

          // Sync any unlinked nodes to IDF
          // Release lock before calling syncNodeToIDF so those calls can acquire it
          if (state.config.createMissingObjects) {
            // Collect nodes that need syncing first
            const nodesToSync = hvacStore.nodes.filter(
              (node) => !get().nodeToObject.has(node.id)
            );

            // Release lock before individual sync calls
            syncLock = false;

            for (const node of nodesToSync) {
              // Each call will acquire/release lock independently
              get().syncNodeToIDF(node.id);
            }

            // Re-acquire lock to complete initial sync
            syncLock = true;
          }

          set({ lastSyncTime: Date.now(), isSyncing: false });
          syncLock = false;
        } catch (error) {
          console.error('Error performing initial sync:', error);
          set({ isSyncing: false, error: String(error) });
          syncLock = false;
        }
      },
    }),
    { name: 'SyncStore' }
  )
);

/**
 * Hook to get sync status for a node
 */
export function useNodeSyncStatus(nodeId: string) {
  const objectId = useSyncStore((state) => state.nodeToObject.get(nodeId));
  const isSyncing = useSyncStore((state) => state.isSyncing);

  return {
    isSynced: !!objectId,
    idfObjectId: objectId,
    isSyncing,
  };
}

/**
 * Hook to get overall sync status
 */
export function useSyncStatus() {
  const enabled = useSyncStore((state) => state.enabled);
  const isSyncing = useSyncStore((state) => state.isSyncing);
  const lastSyncTime = useSyncStore((state) => state.lastSyncTime);
  const conflicts = useSyncStore((state) => state.conflicts);
  const nodeToObject = useSyncStore((state) => state.nodeToObject);
  const error = useSyncStore((state) => state.error);

  return {
    enabled,
    isSyncing,
    lastSyncTime,
    syncedNodeCount: nodeToObject.size,
    hasConflicts: conflicts.length > 0,
    conflictCount: conflicts.length,
    error,
  };
}
